import { GoogleGenerativeAI, HarmCategory, HarmBlockThreshold } from '@google/generative-ai';
import { StreamingChunk, ConversationMessage } from '../types/gemini';

class GeminiService {
  private ai: GoogleGenerativeAI;
  private model: any;
  private readonly modelName = 'gemini-2.5-flash';
  private rateLimitTracker = {
    requestCount: 0,
    tokenCount: 0,
    lastReset: Date.now(),
    rateLimitHit: false
  };

  constructor() {
    const apiKey = import.meta.env?.VITE_GEMINI_API_KEY as string;
    const chatbotEnabled = import.meta.env?.VITE_ENABLE_CHATBOT === 'true';
    
    if (!apiKey || !chatbotEnabled) {
      console.warn('VITE_GEMINI_API_KEY is not configured or chatbot is disabled');
      // Initialize with null values - service will be non-functional but won't crash
      this.ai = null as any;
      this.model = null;
      return;
    }
    
    this.ai = new GoogleGenerativeAI(apiKey);
    this.model = this.ai.getGenerativeModel({ 
      model: this.modelName,
      generationConfig: {
        temperature: 0.7,
        topK: 40,
        topP: 0.95,
        maxOutputTokens: 2048, // Reduced for Flash model efficiency
      },
      safetySettings: [
        {
          category: HarmCategory.HARM_CATEGORY_HARASSMENT,
          threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE
        },
        {
          category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
          threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE
        }
      ]
    });
  }

  async generateStreamingResponse(
    messages: ConversationMessage[],
    systemPrompt: string
  ): Promise<AsyncGenerator<StreamingChunk, void, unknown>> {
    // Return empty generator if service is not initialized
    if (!this.ai || !this.model) {
      return this.generateDisabledResponse();
    }
    
    try {
      // Check rate limits before making request
      await this.checkRateLimits(systemPrompt + messages.map(m => m.parts?.[0]?.text || '').join(''));
      
      console.log('🤖 Starting Gemini streaming request...');
      console.log('Messages count:', messages.length);
      console.log('System prompt length:', systemPrompt.length);
      
      // Combine system prompt with conversation history
      const fullMessages = [
        { role: 'user' as const, parts: [{ text: systemPrompt }] },
        ...messages
      ];

      console.log('📤 Sending to Gemini:', fullMessages);

      // Update rate limit tracker
      this.rateLimitTracker.requestCount++;
      
      const result = await this.model.generateContentStream({
        contents: fullMessages,
      });

      console.log('✅ Gemini response received, processing stream...');
      return this.processStreamingResponse(result);
    } catch (error) {
      console.error('❌ Gemini streaming error:', error);
      
      // Handle rate limit errors specifically
      if (this.isRateLimitError(error)) {
        this.rateLimitTracker.rateLimitHit = true;
        return this.generateRateLimitResponse();
      }
      
      throw new Error(`Failed to generate streaming response: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private async checkRateLimits(inputText: string): Promise<void> {
    const now = Date.now();
    const minutesSinceReset = (now - this.rateLimitTracker.lastReset) / (1000 * 60);
    
    // Reset tracker every minute
    if (minutesSinceReset >= 1) {
      this.rateLimitTracker = {
        requestCount: 0,
        tokenCount: 0,
        lastReset: now,
        rateLimitHit: false
      };
    }
    
    // Check if approaching limits (Free tier: 10 RPM, 250k TPM)
    const estimatedTokens = await this.estimateTokens(inputText);
    
    if (this.rateLimitTracker.requestCount >= 9 || 
        this.rateLimitTracker.tokenCount + estimatedTokens >= 240000) {
      throw new Error('RATE_LIMIT_APPROACHING');
    }
    
    this.rateLimitTracker.tokenCount += estimatedTokens;
  }

  private isRateLimitError(error: any): boolean {
    const errorMessage = error?.message?.toLowerCase() || '';
    const errorCode = error?.code || error?.status;
    
    return errorMessage.includes('rate limit') ||
           errorMessage.includes('quota exceeded') ||
           errorMessage.includes('too many requests') ||
           errorCode === 429 ||
           errorMessage.includes('RATE_LIMIT_APPROACHING');
  }

  private async *generateDisabledResponse(): AsyncGenerator<StreamingChunk, void, unknown> {
    yield {
      text: 'Chat functionality is currently disabled. Please configure VITE_GEMINI_API_KEY and set VITE_ENABLE_CHATBOT=true to enable the AI assistant.',
      isDone: false
    };
    yield {
      text: '',
      isDone: true
    };
  }

  private async *generateRateLimitResponse(): AsyncGenerator<StreamingChunk, void, unknown> {
    const rateLimitMessage = `I apologize, but I've reached my rate limit for now. This helps ensure fair usage for everyone! 

**Please try again in about a minute**, or here are some quick answers to common questions:

## 🔧 **Technical Skills**
- **Python** (Advanced) - Selenium, Pytest, Robot Framework
- **API Testing** - REST, GraphQL, Postman automation  
- **CI/CD** - Jenkins, GitLab CI, Docker containerization
- **Performance Testing** - JMeter, LoadRunner, Gatling

## 💼 **Recent Experience** 
- **Senior Test Engineer** with 5+ years automation expertise
- **Project Leadership** - Led multiple testing initiatives
- **Framework Development** - Built robust test automation from scratch

## 🏆 **Key Achievements**
- Reduced testing time by 70% through automation
- Implemented comprehensive CI/CD testing pipelines
- Mentored junior engineers in best practices

*Thank you for your patience! I'll be ready to answer more detailed questions in just a moment.* ⏰`;

    // Simulate streaming for better UX
    const words = rateLimitMessage.split(' ');
    let currentText = '';
    
    for (let i = 0; i < words.length; i++) {
      currentText += (i === 0 ? '' : ' ') + words[i];
      yield {
        text: currentText,
        finishReason: i === words.length - 1 ? 'STOP' : undefined
      };
      
      // Small delay to simulate streaming
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }

  private async *processStreamingResponse(
    result: any
  ): AsyncGenerator<StreamingChunk, void, unknown> {
    try {
      let hasYieldedContent = false;
      let chunkCount = 0;
      
      for await (const chunk of result.stream) {
        chunkCount++;
        console.log(`📦 Processing chunk ${chunkCount}:`, chunk);
        
        const text = chunk.text();
        if (text) {
          hasYieldedContent = true;
          console.log(`✅ Yielding text chunk: ${text.substring(0, 100)}...`);
          yield {
            text,
            finishReason: chunk.finishReason,
            safetyRatings: chunk.safetyRatings,
          };
        }
      }
      
      if (!hasYieldedContent) {
        console.warn('⚠️ No content was yielded from stream');
        yield {
          text: "I apologize, but I couldn't generate a proper response. Please try rephrasing your question.",
          finishReason: 'STOP',
        };
      }
      
      console.log(`🏁 Stream completed. Total chunks: ${chunkCount}`);
    } catch (error) {
      console.error('❌ Stream processing error:', error);
      yield {
        text: "I apologize, but I'm having trouble responding right now. Please try again.",
        finishReason: 'ERROR',
      };
    }
  }

  async validateMessage(message: string): Promise<boolean> {
    // Always allow messages if service is disabled
    if (!this.ai || !this.model) {
      return true;
    }
    
    console.log('🔍 Validating message:', message.substring(0, 100) + '...');
    
    // Always allow greetings and short messages
    if (message.length < 10) return true;
    
    const lowerMessage = message.toLowerCase();
    
    // Resume-related keywords (more comprehensive)
    const resumeKeywords = [
      'experience', 'skills', 'work', 'job', 'career', 'education',
      'project', 'technology', 'programming', 'testing', 'automation',
      'background', 'qualification', 'expertise', 'role', 'position',
      'responsibility', 'achievement', 'certification', 'training',
      'thanh', 'nguyen', 'resume', 'cv', 'portfolio', 'hello', 'hi',
      'python', 'selenium', 'jenkins', 'api', 'framework', 'ci/cd',
      'quality', 'assurance', 'engineer', 'senior', 'lead', 'manager',
      'hire', 'interview', 'candidate', 'recruit', 'talent'
    ];

    // Off-topic indicators (to block)
    const offTopicKeywords = [
      'weather', 'politics', 'sports', 'movie', 'music', 'cooking',
      'recipe', 'health', 'medical', 'relationship', 'dating',
      'cryptocurrency', 'bitcoin', 'investment', 'gambling',
      'religion', 'personal life', 'family', 'hobby', 'travel'
    ];

    // Check if message contains off-topic content
    const hasOffTopic = offTopicKeywords.some(keyword => 
      lowerMessage.includes(keyword)
    );
    
    if (hasOffTopic) {
      console.log('❌ Message contains off-topic content');
      return false;
    }

    // Check if message is resume-related
    const isResumeRelated = resumeKeywords.some(keyword => 
      lowerMessage.includes(keyword)
    );
    
    if (isResumeRelated) {
      console.log('✅ Message is resume-related');
      return true;
    }

    // For ambiguous messages, allow but log for monitoring
    console.log('⚠️ Ambiguous message - allowing with caution');
    return true;
  }

  generateOffTopicResponse(): string {
    const responses = [
      "I'm here to help you learn about Thanh's professional background! Let me tell you about his **automation testing expertise** instead 🚀",
      "That's outside my expertise! But I'd love to share about Thanh's **Python programming skills** and testing frameworks 💻",
      "I focus on Thanh's career and qualifications. How about we discuss his **5+ years of test automation experience**? 📊",
      "I'm specialized in Thanh's professional profile! Let me highlight his **CI/CD pipeline expertise** instead ⚙️",
      "That's not my area! But I can tell you about Thanh's **project leadership experience** in quality assurance 👨‍💼"
    ];
    
    return responses[Math.floor(Math.random() * responses.length)];
  }

  async estimateTokens(text: string): Promise<number> {
    // Rough estimation: 1 token ≈ 0.75 words
    return Math.ceil(text.split(' ').length / 0.75);
  }

  getModelInfo() {
    return {
      modelName: this.modelName,
      maxTokens: 8192,
      supportedFeatures: ['streaming', 'conversation', 'safety_filtering']
    };
  }
}

export const geminiService = new GeminiService();